/**
 * Audio Effects Processor
 * 
 * Simple, lightweight audio effects using Web Audio API.
 * Currently implements:
 * 1. "Phone Call" EQ filter (Bandpass)
 * 2. "Pivot Voice" (Hyper-fidelity + Glass Room Reverb)
 * 3. "Bitcrush" (Digital distortion)
 * 4. "Delay" (Echo)
 * 5. "Ring Mod" (Robotic textures)
 * 6. "Tremolo" (Amplitude modulation)
 */

let audioContext: AudioContext | null = null;

export function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
}

export function resumeAudioContext(): Promise<void> {
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') {
    return ctx.resume();
  }
  return Promise.resolve();
}

/**
 * Creates a "Phone Call" effect chain.
 * 
 * Simulates a limited frequency response (bandpass) typical of telephone systems.
 * 
 * Chain: Source -> LowCut -> HighCut -> Compressor -> Destination
 */
export function createPhoneCallEffect(sourceNode: AudioNode, destinationNode: AudioNode): void {
  const ctx = sourceNode.context;

  // 1. Low Cut Filter (Highpass) - Remove deep bass
  // Telephones typically cut everything below ~300Hz
  const lowCut = ctx.createBiquadFilter();
  lowCut.type = 'highpass';
  lowCut.frequency.value = 300;
  lowCut.Q.value = 1; // Slight resonance at cutoff

  // 2. High Cut Filter (Lowpass) - Remove high treble
  // Telephones typically cut everything above ~3.4kHz
  const highCut = ctx.createBiquadFilter();
  highCut.type = 'lowpass';
  highCut.frequency.value = 3400;
  highCut.Q.value = 1;

  // 3. Mid Boost (Peaking) - Enhance "nasal" voice frequencies
  const midBoost = ctx.createBiquadFilter();
  midBoost.type = 'peaking';
  midBoost.frequency.value = 1000; // 1kHz center
  midBoost.Q.value = 1;
  midBoost.gain.value = 3; // Slight boost

  // 4. Compressor - Even out dynamics (telephones are heavily compressed)
  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = -24;
  compressor.knee.value = 30;
  compressor.ratio.value = 12;
  compressor.attack.value = 0.003;
  compressor.release.value = 0.25;

  // Connect the chain
  sourceNode.connect(lowCut);
  lowCut.connect(highCut);
  highCut.connect(midBoost);
  midBoost.connect(compressor);
  compressor.connect(destinationNode);
}

export interface PivotVoiceParams {
  // Compressor
  compressorThreshold: number; // -100 to 0
  compressorRatio: number;     // 1 to 20
  
  // Bandpass (Phone-like filtering)
  highPassFreq: number;        // 0 to 2000 Hz
  lowPassFreq: number;         // 1000 to 22000 Hz

  // EQ
  lowShelfFreq: number;        // 20 to 500 Hz
  lowShelfGain: number;        // -20 to 20 dB
  midBandFreq: number;          // 200 to 2000 Hz (Was midCutFreq)
  midBandGain: number;          // -20 to 20 dB (Was midCutGain)
  midBandQ: number;             // 0.1 to 10 (New)
  highShelfFreq: number;       // 2000 to 16000 Hz
  highShelfGain: number;       // -20 to 20 dB

  // Reverb
  reverbWet: number;           // 0 to 1
  reverbDry: number;           // 0 to 1
  reverbDecay: number;         // 0.1 to 5 seconds

  // Distortion / Bitcrush
  bitCrushGain: number;       // 0 to 1 (Mix)
  bitDepth: number;           // 2 to 16 (Resolution)
  sampleRateReduction: number; // 1 to 50 (Downsampling factor)

  // Delay (Echo)
  delayTime: number;          // 0 to 1.0 seconds
  delayFeedback: number;      // 0 to 0.95
  delayWet: number;           // 0 to 1

  // Ring Mod (Robotics)
  ringModFreq: number;        // 0 to 2000 Hz
  ringModMix: number;         // 0 to 1

  // Tremolo (Flutter/Stutter)
  tremoloFreq: number;        // 0 to 20 Hz
  tremoloDepth: number;       // 0 to 1
}

// Default parameters (Clean, Pivot Default)
export const DEFAULT_PIVOT_PARAMS: PivotVoiceParams = {
  compressorThreshold: -20,
  compressorRatio: 4,
  highPassFreq: 20,
  lowPassFreq: 22000,
  lowShelfFreq: 100,
  lowShelfGain: 4.0,
  midBandFreq: 350,
  midBandGain: -2.0,
  midBandQ: 0.8,
  highShelfFreq: 8000,
  highShelfGain: 4.0,
  reverbWet: 0.15,
  reverbDry: 0.9,
  reverbDecay: 0.5,
  bitCrushGain: 0.0,
  bitDepth: 16,
  sampleRateReduction: 1,
  delayTime: 0.3,
  delayFeedback: 0.4,
  delayWet: 0.0,
  ringModFreq: 30,
  ringModMix: 0.0,
  tremoloFreq: 5,
  tremoloDepth: 0.0
};

// PRESETS
export const VOICE_PRESETS: Record<string, PivotVoiceParams> = {
  'pivot-clean': DEFAULT_PIVOT_PARAMS,
  'phone-standard': {
    ...DEFAULT_PIVOT_PARAMS,
    highPassFreq: 300,
    lowPassFreq: 3400,
    midBandFreq: 1000,
    midBandGain: 3.0,
    midBandQ: 1.0,
    lowShelfGain: 0,
    highShelfGain: 0,
    compressorThreshold: -24,
    compressorRatio: 12,
    reverbWet: 0.0,
    reverbDry: 1.0
  },
  'cyber-demon': {
    ...DEFAULT_PIVOT_PARAMS,
    // Deep voice + Ring Mod + Slow Tremolo
    lowShelfGain: 10.0,
    lowShelfFreq: 80,
    ringModFreq: 30, // Low rumble growl
    ringModMix: 0.4, 
    tremoloFreq: 8,
    tremoloDepth: 0.3,
    bitCrushGain: 0.2,
    bitDepth: 10,
    reverbWet: 0.3,
    reverbDecay: 1.5
  },
  'broken-android': {
    ...DEFAULT_PIVOT_PARAMS,
    // Heavy Tremolo stutter
    tremoloFreq: 15, // Very fast flutter
    tremoloDepth: 1.0, // Full chop
    bitCrushGain: 0.3,
    ringModFreq: 600, // Metallic whine
    ringModMix: 0.1,
    reverbWet: 0.0
  },
  'canyon-echo': {
    ...DEFAULT_PIVOT_PARAMS,
    delayTime: 0.5,
    delayFeedback: 0.7, // Lots of repeats
    delayWet: 0.5,
    reverbWet: 0.3,
    reverbDecay: 2.0,
    highShelfGain: -5 // Darken the repeats slightly (simulated via main EQ)
  },
  'dalek': {
    ...DEFAULT_PIVOT_PARAMS,
    // Classic Ring Mod
    ringModFreq: 30, // 30Hz is the magic number for Daleks
    ringModMix: 1.0, // 100% wet
    highPassFreq: 100,
    lowPassFreq: 8000,
    midBandFreq: 500,
    midBandGain: 5.0,
    compressorRatio: 10,
    reverbWet: 0.1
  },
  'phone-payphone': {
    ...DEFAULT_PIVOT_PARAMS,
    highPassFreq: 400,
    lowPassFreq: 3000,
    midBandFreq: 1200,
    midBandGain: 6.0,
    midBandQ: 2.0, 
    compressorThreshold: -30,
    compressorRatio: 16,
    bitCrushGain: 0.1, 
    bitDepth: 12,
    sampleRateReduction: 2,
    reverbWet: 0.05,
    reverbDecay: 0.1 
  },
  'phone-mobile-bad': {
    ...DEFAULT_PIVOT_PARAMS,
    highPassFreq: 200,
    lowPassFreq: 3800,
    midBandGain: 0,
    bitCrushGain: 0.3,
    bitDepth: 6, 
    sampleRateReduction: 3,
    compressorRatio: 8,
    reverbWet: 0
  },
  'intercom': {
    ...DEFAULT_PIVOT_PARAMS,
    highPassFreq: 500,
    lowPassFreq: 2500,
    midBandFreq: 800,
    midBandGain: 10.0,
    midBandQ: 1.5,
    compressorRatio: 20, 
    bitCrushGain: 0.5,
    reverbWet: 0.4,
    reverbDecay: 1.5, 
  },
  'pivot-warm': {
    ...DEFAULT_PIVOT_PARAMS,
    lowShelfGain: 8.0,
    highShelfGain: 2.0,
    reverbWet: 0.1,
    reverbDecay: 0.3
  },
  'glass-sphere': {
    ...DEFAULT_PIVOT_PARAMS,
    reverbWet: 0.6,
    reverbDry: 0.6,
    reverbDecay: 2.5,
    highShelfGain: 8.0
  },
  'retro-computer': {
    ...DEFAULT_PIVOT_PARAMS,
    bitCrushGain: 0.4,
    bitDepth: 8,
    sampleRateReduction: 4,
    reverbWet: 0.0,
    highShelfGain: -10.0,
  },
  'broken-transmission': {
    ...DEFAULT_PIVOT_PARAMS,
    bitCrushGain: 0.8,
    bitDepth: 4,
    sampleRateReduction: 12,
    reverbWet: 0.4,
    reverbDecay: 0.2
  },
  'linkin-park-megaphone': {
    ...DEFAULT_PIVOT_PARAMS,
    highPassFreq: 450,
    lowPassFreq: 4500,
    compressorThreshold: -30,
    compressorRatio: 20,
    lowShelfGain: -10,
    midBandFreq: 1500,
    midBandGain: 12.0,
    midBandQ: 0.7,
    highShelfGain: 4.0,
    bitCrushGain: 0.15,
    bitDepth: 12,
    reverbWet: 0.1,
    reverbDecay: 0.2,
  },
  'ghost-in-the-shell': {
    ...DEFAULT_PIVOT_PARAMS,
    highPassFreq: 150,
    lowPassFreq: 18000, 
    lowShelfGain: 2.0,
    midBandFreq: 2500, 
    midBandGain: 4.0,
    midBandQ: 1.5,
    highShelfGain: 6.0, 
    bitCrushGain: 0.1,  
    bitDepth: 14,       
    sampleRateReduction: 1,
    reverbWet: 0.35,    
    reverbDecay: 1.8,   
    compressorRatio: 2, 
  },
  'hal-9000': {
    ...DEFAULT_PIVOT_PARAMS,
    compressorThreshold: -25,
    compressorRatio: 3, 
    lowShelfFreq: 120,
    lowShelfGain: 5.0, 
    highShelfFreq: 10000,
    highShelfGain: -2.0, 
    reverbWet: 0.0, 
    reverbDry: 1.0,
    bitCrushGain: 0,
  },
  'cortana-telepath': {
    ...DEFAULT_PIVOT_PARAMS,
    lowShelfGain: -5.0, 
    highShelfFreq: 6000,
    highShelfGain: 6.0, 
    reverbWet: 0.5,     
    reverbDry: 0.7,
    reverbDecay: 2.0,   
  },
  'subway-announcer': {
    ...DEFAULT_PIVOT_PARAMS,
    highPassFreq: 200,
    lowPassFreq: 3000, 
    midBandFreq: 500, 
    midBandGain: 6.0,
    bitCrushGain: 0.4, 
    bitDepth: 10,
    reverbWet: 0.5, 
    reverbDecay: 1.5,
  },
  'ghost-in-the-machine': {
    ...DEFAULT_PIVOT_PARAMS,
    // Ethereal, spectral presence
    highPassFreq: 200,
    lowShelfFreq: 150,
    lowShelfGain: -10.0, // Remove body
    highShelfFreq: 6000,
    highShelfGain: 8.0,  // enhance whispery highs
    reverbWet: 0.7,      // Drenched in reverb
    reverbDecay: 3.5,    // Massive space
    reverbDry: 0.4,      // Distant
    delayTime: 0.25,
    delayFeedback: 0.6,
    delayWet: 0.3,
    tremoloFreq: 0.5,    // Slow, breathing pulse
    tremoloDepth: 0.3,
  },
  '8-bit-oracle': {
    ...DEFAULT_PIVOT_PARAMS,
    // Retro synthesis
    highPassFreq: 100,
    lowPassFreq: 6000,
    bitCrushGain: 1.0,   // Full mix
    bitDepth: 4,         // Very lofi
    sampleRateReduction: 12, // Aliasing artifacts
    compressorRatio: 20, // Brickwall limit
    reverbWet: 0.0,      // Completely dry
    midBandFreq: 1000,
    midBandGain: 5.0,    // Poke through
    ringModFreq: 100,    // Sub-bass square wave feel
    ringModMix: 0.1,
  },
  'deep-space': {
    ...DEFAULT_PIVOT_PARAMS,
    // Drifting signal
    highPassFreq: 300,
    lowPassFreq: 2500,   // Narrow band
    reverbWet: 0.5,
    reverbDecay: 4.0,    // Infinite void
    delayTime: 0.8,      // Long delay
    delayFeedback: 0.7,
    delayWet: 0.4,
    tremoloFreq: 0.2,    // Very slow drift
    tremoloDepth: 0.8,   // Deep signal fading
    ringModFreq: 50,
    ringModMix: 0.2,     // Background cosmic radiation
  },
  'am-radio': {
    ...DEFAULT_PIVOT_PARAMS,
    highPassFreq: 400,
    lowPassFreq: 4000,
    bitCrushGain: 0.15, 
    bitDepth: 12,
    sampleRateReduction: 3, 
    compressorRatio: 10, 
    reverbWet: 0.0,
  },
  'asmr-whisper': {
    ...DEFAULT_PIVOT_PARAMS,
    lowShelfFreq: 80,
    lowShelfGain: 12.0, 
    highShelfFreq: 12000,
    highShelfGain: 8.0, 
    midBandFreq: 500,
    midBandGain: -6.0, 
    compressorThreshold: -35, 
    compressorRatio: 8,       
    reverbWet: 0.0, 
    reverbDry: 1.0,
  },
  'airplane-captain': {
    ...DEFAULT_PIVOT_PARAMS,
    highPassFreq: 50,  
    lowPassFreq: 600,  
    lowShelfFreq: 150,
    lowShelfGain: 20.0, 
    midBandFreq: 400,
    midBandGain: 10.0, 
    bitCrushGain: 0.9,  
    bitDepth: 4,        
    sampleRateReduction: 8, 
    compressorThreshold: -50, 
    compressorRatio: 20,      
    reverbWet: 0.0,
  },
  'pivot-trust': {
    ...DEFAULT_PIVOT_PARAMS,
    // "The Gold Standard" - Warm, authoritative, trustworthy.
    // Broadcast-quality EQ curve ("Smile" curve)
    lowShelfFreq: 120,
    lowShelfGain: 5.0,  // Solid foundation/chest voice
    midBandFreq: 500,
    midBandGain: -3.0,  // Remove "mud/boxiness"
    highShelfFreq: 10000,
    highShelfGain: 5.0, // "Air" and expensive mic sheen
    
    // Transparent Dynamics
    compressorThreshold: -20,
    compressorRatio: 3, // Gentle leveling, not squashed
    
    // "Expensive Studio" Room
    reverbWet: 0.12,    // Just enough to not be dry
    reverbDecay: 0.4,   // Tight, treated space
    reverbDry: 1.0,
  },
  'infinite-library': {
    ...DEFAULT_PIVOT_PARAMS,
    // "Vast Intelligence" - Clear, crystalline, spacious.
    // Surgical clarity
    highPassFreq: 80,   // Clean up rumble
    highShelfFreq: 8000,
    highShelfGain: 6.0, // Hyper-articulate
    midBandFreq: 2500,
    midBandGain: 2.0,   // Presence/Intelligibility
    
    // The "Mind Palace" Space
    reverbWet: 0.3,     // Noticeable, lush
    reverbDecay: 1.5,   // Large, noble space (not a cave)
    
    // Thought Echoes
    delayTime: 0.4,
    delayFeedback: 0.3,
    delayWet: 0.15,     // Subtle trailing thoughts
  },
  'warm-analog': {
    ...DEFAULT_PIVOT_PARAMS,
    // "Emotional Connection" - Soft, rounded, comforting.
    // Tape-like response
    lowPassFreq: 14000, // Soften digital harshness
    lowShelfFreq: 150,
    lowShelfGain: 6.0,  // Thick, hugging warmth
    midBandFreq: 800,
    midBandGain: 3.0,   // "Body" of the voice
    
    // Saturation (Tape Glue)
    bitCrushGain: 0.05, // Very subtle harmonic texture
    bitDepth: 16,
    
    // Coziest Reverb
    reverbWet: 0.2,
    reverbDecay: 0.8,   // Wood-paneled room
    
    // Subtle flutter
    tremoloFreq: 4,
    tremoloDepth: 0.05, // Barely perceptible movement
  }
};

// LIVE NODE REGISTRY
// Holds references to active audio nodes to allow real-time parameter modulation.
interface ActiveVoiceNodes {
    compressor: DynamicsCompressorNode;
    highPass: BiquadFilterNode;
    lowShelf: BiquadFilterNode;
    midBand: BiquadFilterNode;
    highShelf: BiquadFilterNode;
    lowPass: BiquadFilterNode;
    reverbDry: GainNode;
    reverbWet: GainNode;
    bitCrushWet: GainNode;
    bitCrushDry: GainNode;
    // New Effects
    delayNode: DelayNode;
    delayFeedback: GainNode;
    delayWet: GainNode;
    ringModOsc: OscillatorNode;
    ringModGain: GainNode;
    tremoloLFO: OscillatorNode;
    tremoloGain: GainNode;
}

let activeNodes: ActiveVoiceNodes | null = null;

// Current active params (mutable for live tweaking)
let currentParams: PivotVoiceParams = { ...DEFAULT_PIVOT_PARAMS };

export function setPivotVoiceParams(params: Partial<PivotVoiceParams>): void {
  currentParams = { ...currentParams, ...params };

  // LIVE UPDATE: Apply to active nodes if they exist
  if (activeNodes) {
      const now = activeNodes.compressor.context.currentTime;
      const rampTime = 0.1; // Smooth transition over 100ms

      // Dynamics
      if (params.compressorThreshold !== undefined) activeNodes.compressor.threshold.setTargetAtTime(params.compressorThreshold, now, rampTime);
      if (params.compressorRatio !== undefined) activeNodes.compressor.ratio.setTargetAtTime(params.compressorRatio, now, rampTime);

      // EQ & Filters
      if (params.highPassFreq !== undefined) activeNodes.highPass.frequency.setTargetAtTime(params.highPassFreq, now, rampTime);
      
      if (params.lowShelfFreq !== undefined) activeNodes.lowShelf.frequency.setTargetAtTime(params.lowShelfFreq, now, rampTime);
      if (params.lowShelfGain !== undefined) activeNodes.lowShelf.gain.setTargetAtTime(params.lowShelfGain, now, rampTime);
      
      if (params.midBandFreq !== undefined) activeNodes.midBand.frequency.setTargetAtTime(params.midBandFreq, now, rampTime);
      if (params.midBandGain !== undefined) activeNodes.midBand.gain.setTargetAtTime(params.midBandGain, now, rampTime);
      if (params.midBandQ !== undefined) activeNodes.midBand.Q.setTargetAtTime(params.midBandQ, now, rampTime);
      
      if (params.highShelfFreq !== undefined) activeNodes.highShelf.frequency.setTargetAtTime(params.highShelfFreq, now, rampTime);
      if (params.highShelfGain !== undefined) activeNodes.highShelf.gain.setTargetAtTime(params.highShelfGain, now, rampTime);

      if (params.lowPassFreq !== undefined) activeNodes.lowPass.frequency.setTargetAtTime(params.lowPassFreq, now, rampTime);

      // Gains
      if (params.reverbDry !== undefined) activeNodes.reverbDry.gain.setTargetAtTime(params.reverbDry, now, rampTime);
      if (params.reverbWet !== undefined) activeNodes.reverbWet.gain.setTargetAtTime(params.reverbWet, now, rampTime);

      if (params.bitCrushGain !== undefined) {
          activeNodes.bitCrushWet.gain.setTargetAtTime(params.bitCrushGain, now, rampTime);
          activeNodes.bitCrushDry.gain.setTargetAtTime(1.0 - params.bitCrushGain, now, rampTime);
      }

      // New Effects Live Update
      if (params.delayTime !== undefined) activeNodes.delayNode.delayTime.setTargetAtTime(params.delayTime, now, rampTime);
      if (params.delayFeedback !== undefined) activeNodes.delayFeedback.gain.setTargetAtTime(params.delayFeedback, now, rampTime);
      if (params.delayWet !== undefined) activeNodes.delayWet.gain.setTargetAtTime(params.delayWet, now, rampTime);

      if (params.ringModFreq !== undefined) activeNodes.ringModOsc.frequency.setTargetAtTime(params.ringModFreq, now, rampTime);
      if (params.ringModMix !== undefined) activeNodes.ringModGain.gain.setTargetAtTime(params.ringModMix, now, rampTime);

      if (params.tremoloFreq !== undefined) activeNodes.tremoloLFO.frequency.setTargetAtTime(params.tremoloFreq, now, rampTime);
      if (params.tremoloDepth !== undefined) activeNodes.tremoloGain.gain.setTargetAtTime(params.tremoloDepth, now, rampTime);
  }
}

export function getPivotVoiceParams(): PivotVoiceParams {
  return { ...currentParams };
}

function createBitCrusher(ctx: AudioContext, bits: number, normfreq: number): AudioNode {
    const bufferSize = 4096;
    const crusher = ctx.createScriptProcessor(bufferSize, 1, 1);
    let phaser = 0;
    let last = 0;

    crusher.onaudioprocess = function(e) {
        const input = e.inputBuffer.getChannelData(0);
        const output = e.outputBuffer.getChannelData(0);
        const step = Math.pow(1/2, bits);
        
        for (let i = 0; i < bufferSize; i++) {
            phaser += normfreq;
            if (phaser >= 1.0) {
                phaser -= 1.0;
                last = step * Math.floor(input[i] / step + 0.5);
            }
            output[i] = last;
        }
    };
    return crusher;
}

/**
 * Creates the "Pivot Voice" effect chain.
 * 
 * Chain: 
 * Source -> Tremolo -> BitCrush -> RingMod -> Compressor -> FilterBank -> Delay -> Reverb -> Dest
 */
export function createPivotVoiceEffect(sourceNode: AudioNode, destinationNode: AudioNode): void {
  const ctx = sourceNode.context;

  // --- 1. Tremolo (Amplitude Modulation) ---
  const tremoloGain = ctx.createGain();
  const tremoloLFO = ctx.createOscillator();
  const tremoloDepthGain = ctx.createGain(); // Scales LFO output
  const tremoloBaseGain = ctx.createGain(); // The actual VCA

  tremoloLFO.frequency.value = currentParams.tremoloFreq;
  tremoloDepthGain.gain.value = currentParams.tremoloDepth;
  
  // Tremolo Logic:
  // Signal * (1 - depth/2 + LFO * depth/2)
  // Actually simpler: Signal goes through a GainNode.
  // GainNode.gain.value = 1.0.
  // LFO (-1 to 1) -> GainNode to modulate it.
  
  // Wiring:
  // Source -> tremoloBaseGain -> ...
  // LFO -> tremoloDepthGain -> tremoloBaseGain.gain
  
  // We want the gain to oscillate between (1-depth) and 1.
  // Or simpler: modulate around 1.
  tremoloBaseGain.gain.value = 1.0;
  tremoloLFO.connect(tremoloDepthGain);
  tremoloDepthGain.connect(tremoloBaseGain.gain);
  
  tremoloLFO.start();


  // --- 2. BitCrusher ---
  const bitCrushInput = ctx.createGain();
  const bitCrushWet = ctx.createGain();
  const bitCrushDry = ctx.createGain();
  const bitCrushMerge = ctx.createGain();

  bitCrushWet.gain.value = currentParams.bitCrushGain;
  bitCrushDry.gain.value = 1.0 - currentParams.bitCrushGain;

  if (currentParams.bitCrushGain > 0) {
      const crusher = createBitCrusher(ctx, currentParams.bitDepth, 1 / currentParams.sampleRateReduction);
      bitCrushInput.connect(crusher);
      crusher.connect(bitCrushWet);
  } else {
      bitCrushInput.connect(bitCrushWet);
  }
  bitCrushInput.connect(bitCrushDry);
  bitCrushDry.connect(bitCrushMerge);
  bitCrushWet.connect(bitCrushMerge);


  // --- 3. Ring Modulator ---
  // Signal * Oscillator
  const ringModInput = ctx.createGain();
  const ringModWet = ctx.createGain();
  const ringModDry = ctx.createGain();
  const ringModMerge = ctx.createGain();
  
  const ringModOsc = ctx.createOscillator();
  ringModOsc.type = 'sine';
  ringModOsc.frequency.value = currentParams.ringModFreq;
  ringModOsc.start();

  // Multiply signal by oscillator
  const ringModMult = ctx.createGain();
  ringModMult.gain.value = 0; // Controlled by Oscillator
  
  // Actually Ring Mod is Input -> GainNode(Gain modulated by Osc)
  // Input connected to GainNode input.
  // Osc connected to GainNode.gain.
  // Result is Input * Osc.
  
  // Wiring Ring Mod:
  // Input -> ringModMult
  // Osc -> ringModMult.gain
  ringModMult.gain.value = 0; // Audio rate modulation requires base 0? No, it adds.
  // For true multiplication: Gain node with value 0, modulated by AudioParam.
  // BUT WebAudio AudioParams add.
  // So we need:
  // Signal -> GainNode (Gain = 0)
  // Osc -> GainNode.gain
  
  ringModOsc.connect(ringModMult.gain);

  ringModInput.connect(ringModMult); // Wet Path
  ringModMult.connect(ringModWet);

  ringModInput.connect(ringModDry); // Dry Path
  
  ringModWet.gain.value = currentParams.ringModMix;
  ringModDry.gain.value = 1.0 - currentParams.ringModMix;

  ringModWet.connect(ringModMerge);
  ringModDry.connect(ringModMerge);


  // --- 4. Dynamics (Compressor) ---
  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = currentParams.compressorThreshold;
  compressor.knee.value = 10;
  compressor.ratio.value = currentParams.compressorRatio;
  compressor.attack.value = 0.005;
  compressor.release.value = 0.1;


  // --- 5. Filter Bank ---
  const highPass = ctx.createBiquadFilter();
  highPass.type = 'highpass';
  highPass.frequency.value = currentParams.highPassFreq;
  highPass.Q.value = 1.0;

  const lowShelf = ctx.createBiquadFilter();
  lowShelf.type = 'lowshelf';
  lowShelf.frequency.value = currentParams.lowShelfFreq;
  lowShelf.gain.value = currentParams.lowShelfGain;

  const midBand = ctx.createBiquadFilter();
  midBand.type = 'peaking';
  midBand.frequency.value = currentParams.midBandFreq;
  midBand.Q.value = currentParams.midBandQ;
  midBand.gain.value = currentParams.midBandGain;

  const highShelf = ctx.createBiquadFilter();
  highShelf.type = 'highshelf';
  highShelf.frequency.value = currentParams.highShelfFreq;
  highShelf.gain.value = currentParams.highShelfGain;

  const lowPass = ctx.createBiquadFilter();
  lowPass.type = 'lowpass';
  lowPass.frequency.value = currentParams.lowPassFreq;
  lowPass.Q.value = 1.0;


  // --- 6. Delay (Echo) ---
  const delayNode = ctx.createDelay(2.0); // Max 2s
  delayNode.delayTime.value = currentParams.delayTime;
  
  const delayFeedback = ctx.createGain();
  delayFeedback.gain.value = currentParams.delayFeedback;
  
  const delayWet = ctx.createGain();
  delayWet.gain.value = currentParams.delayWet;
  
  // Filter for delay repeats (optional, keeps it clean)
  // delayNode -> delayFeedback -> delayNode
  delayNode.connect(delayFeedback);
  delayFeedback.connect(delayNode);


  // --- 7. Reverb (Convolver) ---
  const convolver = ctx.createConvolver();
  convolver.buffer = getGlassRoomImpulse(ctx, currentParams.reverbDecay);
  
  const dryGain = ctx.createGain();
  const wetGain = ctx.createGain();
  dryGain.gain.value = currentParams.reverbDry;
  wetGain.gain.value = currentParams.reverbWet;


  // === MAIN CHAIN CONNECTION ===
  
  // Source -> Tremolo
  sourceNode.connect(tremoloBaseGain);
  
  // Tremolo -> BitCrush
  tremoloBaseGain.connect(bitCrushInput);
  
  // BitCrush -> RingMod
  bitCrushMerge.connect(ringModInput);
  
  // RingMod -> Compressor
  ringModMerge.connect(compressor);
  
  // Compressor -> Filters
  compressor.connect(highPass);
  highPass.connect(lowShelf);
  lowShelf.connect(midBand);
  midBand.connect(highShelf);
  highShelf.connect(lowPass);

  // Filters -> Split (Dry, Delay, Reverb)
  // 1. Main Dry Path
  lowPass.connect(dryGain);
  
  // 2. Reverb Path
  lowPass.connect(convolver);
  convolver.connect(wetGain);
  
  // 3. Delay Path
  lowPass.connect(delayNode);
  delayNode.connect(delayWet);

  // Recombine all to destination
  dryGain.connect(destinationNode);
  wetGain.connect(destinationNode);
  delayWet.connect(destinationNode);


  // STORE ACTIVE NODES FOR LIVE MODULATION
  activeNodes = {
      compressor,
      highPass,
      lowShelf,
      midBand,
      highShelf,
      lowPass,
      reverbDry: dryGain,
      reverbWet: wetGain,
      bitCrushDry,
      bitCrushWet,
      delayNode,
      delayFeedback,
      delayWet,
      ringModOsc,
      ringModGain: ringModWet, // We modulate the Wet mix
      tremoloLFO,
      tremoloGain: tremoloDepthGain
  };
}

// Helper to cache/generate impulse
let glassImpulse: AudioBuffer | null = null;
let glassImpulseDecay: number = -1;

function getGlassRoomImpulse(ctx: AudioContext, decayTime: number = 0.5): AudioBuffer {
    if (glassImpulse && glassImpulseDecay === decayTime) return glassImpulse;

    const rate = ctx.sampleRate;
    const length = rate * decayTime;
    const impulse = ctx.createBuffer(2, length, rate);
    const left = impulse.getChannelData(0);
    const right = impulse.getChannelData(1);

    for (let i = 0; i < length; i++) {
        // Exponential decay
        const decay = Math.pow(1 - i / length, 8);
        
        // Noise
        left[i] = (Math.random() * 2 - 1) * decay;
        right[i] = (Math.random() * 2 - 1) * decay;
    }
    glassImpulse = impulse;
    glassImpulseDecay = decayTime;
    return impulse;
}
